import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, ExternalLink } from 'lucide-react';
import { ProjectForm } from './ProjectForm';

const API = process.env.REACT_APP_BACKEND_URL;
const PRESETS = ['1st Place', '2nd Place', '3rd Place', 'Best Design', 'People’s Choice'];

// Organizer: pick winners, edit projects, open/close submissions, publish the public showcase
export const CompanyShowcasePanel = ({ eventId, headers }) => {
  const [data, setData] = useState(null);
  const [awards, setAwards] = useState({});
  const [editing, setEditing] = useState(null);
  const url = `${API}/api/event-teams/company/${eventId}/showcase`;

  const load = async (resetAwards = true) => {
    try {
      const r = await axios.get(url, { headers });
      setData(r.data);
      const saved = Object.fromEntries((r.data.showcase.awards || []).map((a) => [a.team_id, a.label]));
      setAwards((cur) => (resetAwards ? saved : { ...saved, ...cur }));
    } catch { toast.error('Failed to load showcase'); }
  };
  useEffect(() => { load(); }, [eventId]);

  const save = async (extra = {}) => {
    const list = Object.entries(awards).filter(([, l]) => l && l.trim()).map(([team_id, label]) => {
      const i = PRESETS.indexOf(label);
      return { team_id, label, rank: i === -1 ? 50 : i + 1 };
    });
    try {
      const r = await axios.put(url, { awards: list, ...extra }, { headers });
      toast.success(r.data.message); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  if (!data) return <div className="py-10 text-center text-sm text-[#666]">Loading…</div>;
  const sc = data.showcase;
  return (
    <div className="space-y-4" data-testid="company-showcase-panel">
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <Trophy size={18} className="text-amber-500" />
        <p className="text-sm font-semibold text-[#191919] mr-auto">Showcase is {sc.published ? <span className="text-emerald-700" data-testid="showcase-status">published</span> : <span className="text-[#666]" data-testid="showcase-status">not published</span>}</p>
        <label className="flex items-center gap-2 text-sm text-[#191919]">
          <input type="checkbox" checked={sc.submissions_open} onChange={(e) => save({ submissions_open: e.target.checked })} data-testid="submissions-open-toggle" /> Accept project submissions
        </label>
        <button onClick={() => save()} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-[#191919]" data-testid="save-awards-btn">Save winners</button>
        <button onClick={() => save({ published: !sc.published })} className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${sc.published ? 'bg-gray-600' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="publish-showcase-btn">
          {sc.published ? 'Unpublish' : 'Publish showcase'}
        </button>
      </div>
      <datalist id="award-presets">{PRESETS.map((p) => <option key={p} value={p} />)}</datalist>
      {data.teams.length === 0 ? <p className="text-sm text-[#666] text-center py-8">No locked or confirmed teams yet.</p> : data.teams.map((t) => (
        <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`showcase-team-${t.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="mr-auto min-w-0">
              <p className="font-semibold text-[#191919]">{t.name} <span className="text-xs text-[#666] font-normal">· {t.member_count} members · {t.status}</span></p>
              {t.project ? (
                <p className="text-sm text-[#666] truncate">{t.project.title}{t.project.demo_url && <a href={t.project.demo_url} target="_blank" rel="noreferrer" className="inline-flex ml-2 text-[#0a66c2]"><ExternalLink size={13} /></a>}</p>
              ) : <p className="text-xs text-amber-700">No project submitted yet</p>}
            </div>
            <input list="award-presets" value={awards[t.id] || ''} onChange={(e) => setAwards({ ...awards, [t.id]: e.target.value })}
              placeholder="Award (e.g. 1st Place)" className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-48 text-[#191919]" data-testid={`award-input-${t.id}`} />
            <button onClick={() => setEditing(editing === t.id ? null : t.id)} className="text-sm text-[#0a66c2] font-medium" data-testid={`edit-project-${t.id}`}>{editing === t.id ? 'Close' : t.project ? 'Edit project' : 'Add project'}</button>
          </div>
          {editing === t.id && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <ProjectForm initial={t.project} headers={headers} testPrefix={`org-project-${t.id}`}
                saveUrl={`${API}/api/event-teams/company/${eventId}/teams/${t.id}/project`} onSaved={() => { setEditing(null); load(false); }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
